import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Hero.css';

gsap.registerPlugin(ScrollTrigger);

const Hero: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // Particles
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    let particles: any[] = [];

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };

    class Particle {
      x: number; y: number; vx: number; vy: number;
      r: number; alpha: number; color: string;
      constructor() {
        this.x = Math.random() * W;
        this.y = Math.random() * H;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.r = Math.random() * 1.5 + 0.3;
        this.alpha = Math.random() * 0.5 + 0.1;
        this.color = Math.random() > 0.5 ? '#bf00ff' : '#00ccff';
      }
      reset() {
        this.x = Math.random() * W;
        this.y = Math.random() * H;
      }
    }

    const init = () => {
      particles = [];
      const count = Math.floor((W * H) / 7000);
      for (let i = 0; i < count; i++) particles.push(new Particle());
    };

    const drawConnections = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 130) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(191,0,255,${0.07 * (1 - d / 130)})`;
            ctx.lineWidth = 0.4;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    let raf: number;
    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      drawConnections();
      particles.forEach((p: any) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > W || p.y < 0 || p.y > H) p.reset();
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      raf = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    init();
    animate();

    // GSAP animations
    const tl = gsap.timeline({ delay: 0.3 });
    tl.fromTo(titleRef.current,
      { y: 80, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.2, ease: 'power4.out' }
    )
    .fromTo(subtitleRef.current,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }, '-=0.6'
    )
    .fromTo(ctaRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, '-=0.5'
    );

    // Parallax on scroll
    gsap.to(heroRef.current, {
      scrollTrigger: {
        trigger: heroRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
      y: 200,
      opacity: 0,
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="hero" ref={heroRef}>
      <canvas ref={canvasRef} className="hero-canvas" />
      <div className="hero-scanlines" />
      <div className="hero-vignette" />

      <div className="hero-content">
        <div className="hero-badge">
          <span className="hero-dot" />
          <span>Forgiamo il futuro</span>
        </div>

        <h1 ref={titleRef} className="hero-title">
          <span className="hero-kro">Kro</span>
          <span className="hero-maker">Maker</span>
        </h1>

        <p ref={subtitleRef} className="hero-subtitle">
          Luce · Materia · Tecnologia
        </p>

        <p className="hero-desc">
          Stampa 3D, incisione laser, stampa UV e sviluppo tecnologico.<br />
          Trasformiamo le tue idee in realtà.
        </p>

        <div ref={ctaRef} className="hero-cta">
          <button className="btn-primary" onClick={() => scrollTo('services')}>
            Scopri i servizi
          </button>
          <button className="btn-secondary" onClick={() => scrollTo('contact')}>
            Inizia un progetto
          </button>
        </div>

        <div className="hero-scroll-indicator">
          <div className="scroll-line" />
          <span>Scorri</span>
        </div>
      </div>

      <div className="hero-corners">
        <div className="corner tl" />
        <div className="corner tr" />
        <div className="corner bl" />
        <div className="corner br" />
      </div>
    </section>
  );
};

export default Hero;
